import { toast } from "react-toastify";
import { Get, Postdata } from "./api.services";
import HttpService from "./https.services";

const FANTASY_CONTEST_LIST_PATHS = new Set([
  "/fantasy",
  "/fantasy/private",
  "/fantasy/multi-round",
  "/fantasy/multi-round/private",
]);

const HISTORY_IDX_KEY = "fantasyAllContestsHistoryIdx";
const HISTORY_PATH_KEY = "fantasyAllContestsPath";

export function rememberFantasyContestsHistory(pathname) {
  if (!FANTASY_CONTEST_LIST_PATHS.has(pathname)) return;

  const idx = window.history.state?.idx;
  if (Number.isInteger(idx)) {
    sessionStorage.setItem(HISTORY_IDX_KEY, String(idx));
  }
  sessionStorage.setItem(HISTORY_PATH_KEY, pathname);
}

// Joining a contest pushes a history entry for every screen. Back from Invite
// should return to All Contests in one step instead of replaying those screens.
export function backToFantasyContests(navigate) {
  const savedRaw = sessionStorage.getItem(HISTORY_IDX_KEY);
  const savedIdx = Number(savedRaw);
  const currentIdx = window.history.state?.idx;
  const savedPath = sessionStorage.getItem(HISTORY_PATH_KEY);
  const fallbackPath = FANTASY_CONTEST_LIST_PATHS.has(savedPath)
    ? savedPath
    : "/fantasy";

  if (
    savedRaw != null &&
    Number.isInteger(savedIdx) &&
    Number.isInteger(currentIdx) &&
    currentIdx > savedIdx
  ) {
    navigate(savedIdx - currentIdx);
    return;
  }

  navigate(fallbackPath, { replace: true });
}

export function buildFantasyShareUrl({ id, code, round }) {
  const params = new URLSearchParams();
  if (round === "single" || round === "multi") {
    params.set("fantasyType", round);
  }
  if (id) {
    params.set(round === "multi" ? "fantasyId" : "id", String(id));
  }
  if (code) {
    params.set("fantasyCode", code);
  }
  return `${window.location.origin}/?${params.toString()}`;
}

export function readFantasyShareParams(
  search = typeof window !== "undefined" ? window.location.search : "",
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
) {
  const params = new URLSearchParams(search);
  let fantasyType = params.get("fantasyType");
  let code = params.get("fantasyCode") || params.get("code");
  let id =
    params.get("fantasyId") || params.get("id") || params.get("contestId");

  if (!id && !code && pathname) {
    const idMatch = pathname.match(/(?:^\/|\/)(?:fantasyId|contestId|id)=([^/?#]+)/i);
    if (idMatch) {
      id = decodeURIComponent(idMatch[1]);
      if (/fantasyId=/i.test(pathname)) {
        fantasyType = fantasyType || "multi";
      }
    }
    const codeMatch = pathname.match(/(?:^\/|\/)(?:fantasyCode|code|invite_code)=([^/?#]+)/i);
    if (codeMatch) {
      code = decodeURIComponent(codeMatch[1]);
    }
    if (!id && !code) {
      const slashMatch = pathname.match(/(?:^\/|\/)(?:fantasyId|contestId)\/([^/?#]+)/i);
      if (slashMatch) {
        id = decodeURIComponent(slashMatch[1]);
        fantasyType = fantasyType || "multi";
      }
    }
  }

  if (typeof window !== "undefined") {
    try {
      if (id || code) {
        sessionStorage.setItem(
          "pendingFantasyShare",
          JSON.stringify({ code: code || null, id: id || null, fantasyType: fantasyType || null })
        );
      } else {
        const stored = sessionStorage.getItem("pendingFantasyShare");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.id || parsed?.code) {
            id = parsed.id || id;
            code = parsed.code || code;
            fantasyType = parsed.fantasyType || fantasyType;
          }
        }
      }
    } catch (_) {}
  }

  return { code, id, fantasyType };
}

export function hasFantasyShareParams(
  search = typeof window !== "undefined" ? window.location.search : "",
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
) {
  const { code, id } = readFantasyShareParams(search, pathname);
  return Boolean(code || id);
}

export function fantasyShareSearch(
  search = typeof window !== "undefined" ? window.location.search : "",
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
) {
  const { code, id, fantasyType } = readFantasyShareParams(search, pathname);
  const params = new URLSearchParams();
  if (fantasyType === "single" || fantasyType === "multi") {
    params.set("fantasyType", fantasyType);
  }
  if (code) params.set("fantasyCode", code);
  if (id) params.set(fantasyType === "multi" ? "fantasyId" : "id", id);
  const query = params.toString();
  return query ? `?${query}` : "";
}

function shareVisibility(data, code) {
  if (data?.type === "private" || data?.type === "public") return data.type;
  if (data?.is_private === 1 || data?.is_private === true) return "private";
  if (data?.is_private === 0 || data?.is_private === false) return "public";
  return code ? "private" : "public";
}

function unicodeStyledToAscii(char) {
  const cp = char.codePointAt(0);
  const digitStarts = [0x1d7ce, 0x1d7d8, 0x1d7e2, 0x1d7ec, 0x1d7f6];
  for (const start of digitStarts) {
    if (cp >= start && cp <= start + 9) {
      return String.fromCharCode(48 + (cp - start));
    }
  }
  if (cp >= 0x1d400 && cp <= 0x1d419) {
    return String.fromCharCode(65 + (cp - 0x1d400));
  }
  if (cp >= 0x1d41a && cp <= 0x1d433) {
    return String.fromCharCode(97 + (cp - 0x1d41a));
  }
  return char;
}

export function normalizeInviteCode(code) {
  const raw = String(code || "").trim();
  if (!raw) return "";

  try {
    const url = new URL(raw);
    const fromUrl =
      url.searchParams.get("fantasyCode") ||
      url.searchParams.get("code") ||
      url.searchParams.get("invite_code");
    if (fromUrl) return normalizeInviteCode(fromUrl);
  } catch (_) {
    const qsMatch = raw.match(
      /[?&](?:fantasyCode|code|invite_code)=([^&\s]+)/i
    );
    if (qsMatch) {
      return normalizeInviteCode(decodeURIComponent(qsMatch[1]));
    }
  }

  const fromLabel = raw.match(/invite\s*code[:\s]+([^\s\n]+)/i);
  const candidate = fromLabel ? fromLabel[1] : raw;

  return Array.from(candidate)
    .map(unicodeStyledToAscii)
    .join("")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function openSharedContest(navigate, data, code, { replace = true } = {}) {
  const contestType = shareVisibility(data, code);
  navigate(`/fantasy/leaguedetail/${data.id}`, {
    replace,
    state: {
      contest: data,
      contestType,
      InviteJoin: contestType === "private" ? data?.invite_code : undefined,
      contestStatus: data?.contestStatus,
    },
  });
}

function openSharedMultiFantasy(navigate, data, code, { replace = true } = {}) {
  const fantasyType = shareVisibility(data, code);
  navigate(`/fantasy/multi-round/leaguedetail/${data.id}`, {
    replace,
    state: {
      fantasy: data,
      fantasyType,
      InviteJoin: fantasyType === "private" ? data?.invite_code : undefined,
      contestStatus: data?.fantasy_status,
    },
  });
}

function recordId(data) {
  return data?.id || data?.dataValues?.id || data?.fantasy_id || data?.contest_id;
}

function asResolved(res, kind) {
  const id = recordId(res?.data);
  if (!res?.status || !id) return null;
  return {
    kind,
    data: { ...res.data, id },
    message: res.message,
  };
}

export function resolveFantasyInviteCode(code) {
  const trimmed = String(code || "").trim();
  const normalized = normalizeInviteCode(code);
  const codes = [...new Set([trimmed, normalized].filter(Boolean))];
  if (!codes.length) return Promise.resolve(null);

  const attempts = codes.flatMap((inviteCode) => [
    () =>
      Postdata(HttpService().contestDetails, {
        code: inviteCode,
        invite_code: inviteCode,
        return_type: "fantasy",
      }).then((res) => asResolved(res, "single")),
    () =>
      Get(HttpService().fantasyDetails, {
        params: { code: inviteCode, invite_code: inviteCode },
      }).then((res) => asResolved(res, "multi")),
  ]);

  const run = (index) => {
    if (index >= attempts.length) return Promise.resolve(null);
    return attempts[index]()
      .catch(() => null)
      .then((found) => found || run(index + 1));
  };

  return run(0);
}

export function navigateToResolvedFantasy(navigate, resolved, { replace = false } = {}) {
  if (!resolved?.data?.id) return false;
  if (resolved.kind === "multi") {
    openSharedMultiFantasy(navigate, resolved.data, resolved.data.invite_code, { replace });
  } else {
    openSharedContest(navigate, resolved.data, resolved.data.invite_code, { replace });
  }
  return true;
}

export function openSharedFantasyLink(
  navigate,
  search = typeof window !== "undefined" ? window.location.search : "",
  pathname = typeof window !== "undefined" ? window.location.pathname : ""
) {
  const { code, id, fantasyType } = readFantasyShareParams(search, pathname);
  if (!code && !id) return Promise.resolve(false);

  const clearPendingShare = () => {
    if (typeof window !== "undefined") {
      try {
        sessionStorage.removeItem("pendingFantasyShare");
      } catch (_) {}
    }
  };

  const fail = () => {
    clearPendingShare();
    toast.warn("Invalid link! Please check your link");
    navigate("/home", { replace: true });
  };

  const loadContest = () => {
    const payload = code
      ? { code: normalizeInviteCode(code), return_type: "fantasy" }
      : { contest_id: id, return_type: "fantasy" };
    return Postdata(HttpService().contestDetails, payload).then((res) => {
      if (res?.status && res?.data?.id) {
        clearPendingShare();
        openSharedContest(navigate, res.data, code);
        return true;
      }
      return false;
    });
  };

  const loadFantasy = () => {
    const params = code
      ? { code: normalizeInviteCode(code) }
      : { fantasy_id: id };
    return Get(HttpService().fantasyDetails, { params }).then((response) => {
      if (response?.status && response?.data?.id) {
        clearPendingShare();
        openSharedMultiFantasy(navigate, response.data, code);
        return true;
      }
      return false;
    });
  };

  const attempt = async () => {
    if (fantasyType === "single") {
      if (await loadContest()) return true;
      return loadFantasy();
    }
    if (fantasyType === "multi") {
      if (await loadFantasy()) return true;
      return loadContest();
    }
    try {
      if (await loadFantasy()) return true;
    } catch (error) {
      // Single-round contests are stored on the contest API.
    }
    return loadContest();
  };

  return attempt()
    .then((opened) => {
      if (!opened) fail();
      return opened;
    })
    .catch(() => {
      fail();
      return false;
    });
}
